import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MessageCircle, RefreshCw, QrCode, Unplug, CheckCircle2, AlertTriangle, Loader2, Send } from 'lucide-react';
import { apiFetch } from '../lib/api';

interface WaStatus {
  state: 'ok' | 'down' | 'unknown';
  sender: string;
  configured: boolean;
  lastOutboundStatus: string | null;
  lastOutboundError: string | null;
  lastOutboundAt: number;
  lastInboundAt: number;
  sent24h: number;
  failed24h: number;
  consecutiveFailures: number;
}

type QrState = 'idle' | 'processing' | 'qr' | 'connected' | 'error';

const fmtTs = (ts: number) => (ts > 0 ? new Date(ts * 1000).toLocaleString('zh-HK') : '從未有記錄');

const AdminWhatsAppTab: React.FC = () => {
  const [status, setStatus] = useState<WaStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [canaryRunning, setCanaryRunning] = useState(false);
  const [canaryResult, setCanaryResult] = useState<{ ok: boolean; error?: string } | null>(null);

  const [qrState, setQrState] = useState<QrState>('idle');
  const [qrImage, setQrImage] = useState('');
  const [qrMessage, setQrMessage] = useState('');
  const [unbinding, setUnbinding] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // 營運用測試發送：向指定號碼直發任意訊息（驗證通知格式／新模板文案）
  const [testTo, setTestTo] = useState('');
  const [testMessage, setTestMessage] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; error?: string } | null>(null);

  const handleTestSend = async () => {
    const to = testTo.replace(/\s/g, '');
    const message = testMessage.trim();
    if (!to || !message) {
      setTestResult({ ok: false, error: '請填目標號碼與訊息內容' });
      return;
    }
    setTestSending(true);
    setTestResult(null);
    try {
      const res = await apiFetch('/api/public/admin/whatsapp/test-send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to, message }),
      });
      const json = await res.json().catch(() => ({}));
      const data = json.data ?? json;
      if (res.ok && data.ok !== undefined) {
        setTestResult({ ok: !!data.ok, error: data.error });
        fetchStatus();
      } else {
        setTestResult({ ok: false, error: data.error || `HTTP ${res.status}` });
      }
    } catch (e) {
      setTestResult({ ok: false, error: e instanceof Error ? e.message : '網絡錯誤' });
    } finally {
      setTestSending(false);
    }
  };


  const fetchStatus = useCallback(async () => {
    try {
      const res = await apiFetch('/api/public/admin/whatsapp/status');
      const json = await res.json().catch(() => ({}));
      const data = json.data ?? json;
      if (res.ok) setStatus(data);
    } catch {
      // keep previous status
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };
  useEffect(() => stopPolling, []);

  const pollQr = useCallback(async () => {
    try {
      const res = await apiFetch('/api/public/admin/whatsapp/generate-qr', { method: 'POST' });
      const json = await res.json().catch(() => ({}));
      const data = json.data ?? json;
      if (!res.ok) {
        setQrState('error');
        setQrMessage(data.error || '產生 QR 失敗');
        stopPolling();
        return;
      }
      if (data.state === 'qr') {
        setQrState('qr');
        setQrImage(data.qr);
      } else if (data.state === 'connected') {
        setQrState('connected');
        stopPolling();
        fetchStatus();
      } else if (data.state === 'processing') {
        setQrState('processing');
      } else {
        setQrState('error');
        setQrMessage(data.message || '未知回應');
        stopPolling();
      }
    } catch {
      // 網絡抖動：繼續輪詢
    }
  }, [fetchStatus]);

  const startRebind = () => {
    stopPolling();
    setQrState('processing');
    setQrImage('');
    setQrMessage('');
    pollQr();
    pollRef.current = setInterval(pollQr, 4000);
  };

  const handleUnbind = async () => {
    if (!confirm('確定解除綁定？解除後 WhatsApp 收發會即時停止，需要重新掃碼先恢復。')) return;
    setUnbinding(true);
    try {
      const res = await apiFetch('/api/public/admin/whatsapp/unbind', { method: 'POST' });
      const json = await res.json().catch(() => ({}));
      const data = json.data ?? json;
      if (res.ok && data.state === 'disconnected') {
        setQrState('idle');
        setQrMessage('');
        alert('已解除綁定。請撳「重新連接 WhatsApp」掃碼。');
        fetchStatus();
      } else {
        alert(`解除失敗：${data.message || data.error || '未知錯誤'}`);
      }
    } finally {
      setUnbinding(false);
    }
  };

  const handleCanary = async () => {
    setCanaryRunning(true);
    setCanaryResult(null);
    try {
      const res = await apiFetch('/api/public/admin/whatsapp/canary', { method: 'POST' });
      const json = await res.json().catch(() => ({}));
      const data = json.data ?? json;
      setCanaryResult({ ok: !!data.ok, error: data.error });
      fetchStatus();
    } catch (e) {
      setCanaryResult({ ok: false, error: e instanceof Error ? e.message : '網絡錯誤' });
    } finally {
      setCanaryRunning(false);
    }
  };

  const stateBadge = !status ? null : status.state === 'ok'
    ? <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-bold bg-green-100 text-green-700"><CheckCircle2 size={16} /> 已連線</span>
    : status.state === 'down'
      ? <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-bold bg-red-100 text-red-700"><AlertTriangle size={16} /> 疑似斷線</span>
      : <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-bold bg-gray-100 text-gray-600">未有足夠數據</span>;

  return (
    <div className="space-y-6">
      {/* 狀態卡 */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <MessageCircle size={20} className="text-green-600" />
            WhatsApp 連線狀態
          </h2>
          <button
            onClick={() => { setLoading(true); fetchStatus(); }}
            className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 flex items-center gap-1"
          >
            <RefreshCw size={14} /> 重新整理
          </button>
        </div>

        {loading ? (
          <div className="text-gray-400 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> 讀取中…</div>
        ) : !status?.configured ? (
          <div className="text-red-600 text-sm">未設定 CLOUDWAPI_KEY／CLOUDWAPI_SENDER，請檢查 Cloudflare Secrets。</div>
        ) : (
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-3">{stateBadge}<span className="text-gray-500 dark:text-gray-400">發送號碼：{status?.sender}</span></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-gray-600 dark:text-gray-300">
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3">最近成功發送<span className="block font-bold mt-1">{status?.sent24h ? `${status.sent24h} 次（24h 內）` : '24h 內無'}</span></div>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3">最近 24h 失敗<span className={`block font-bold mt-1 ${status?.failed24h ? 'text-red-600' : ''}`}>{status?.failed24h ?? 0} 次{status?.consecutiveFailures ? `（連續 ${status.consecutiveFailures} 次）` : ''}</span></div>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3">最後發送<span className="block font-bold mt-1">{fmtTs(status?.lastOutboundAt || 0)}{status?.lastOutboundStatus === 'failed' ? '（失敗）' : ''}</span></div>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3">最後收到客訊<span className="block font-bold mt-1">{fmtTs(status?.lastInboundAt || 0)}</span></div>
            </div>
            {status?.lastOutboundError && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 rounded-lg p-3 text-xs">
                最近一次發送錯誤：{status.lastOutboundError}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 pt-2">
              <button
                onClick={handleCanary}
                disabled={canaryRunning}
                className="inline-flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-green-700 disabled:opacity-50 transition"
              >
                {canaryRunning ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                即時測試連線（發自檢訊息去 {status?.sender}）
              </button>
              {canaryResult && (
                canaryResult.ok
                  ? <span className="text-green-600 text-sm font-bold">✓ 發送成功，通道正常</span>
                  : <span className="text-red-600 text-sm font-bold">✗ 發送失敗：{canaryResult.error}</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* 測試發送 */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2 mb-2">
          <Send size={20} className="text-red-600" />
          測試發送訊息
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          向指定號碼直發任意訊息——驗證通知格式、測試新模板文案，或者發去 admin 群組（填群組 JID，如 852XXXXXXXX-XXXX@g.us）。
        </p>
        <div className="space-y-3 max-w-xl">
          <input
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="目標號碼（如 8526XXXXXXX）或群組 JID"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none dark:bg-gray-800 dark:text-white"
          />
          <textarea
            value={testMessage}
            onChange={(e) => setTestMessage(e.target.value)}
            placeholder="訊息內容（支援換行；最長 1000 字）"
            rows={5}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none dark:bg-gray-800 dark:text-white"
          />
          <div className="flex items-center gap-3">
            <button
              onClick={handleTestSend}
              disabled={testSending}
              className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-red-700 disabled:opacity-50 transition"
            >
              {testSending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              發送測試訊息
            </button>
            {testResult && (
              testResult.ok
                ? <span className="text-green-600 text-sm font-bold">✓ 已發送</span>
                : <span className="text-red-600 text-sm font-bold">✗ 失敗：{testResult.error}</span>
            )}
          </div>
        </div>
      </div>

      {/* 重新連接 */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2 mb-2">
          <QrCode size={20} className="text-gray-600 dark:text-gray-300" />
          重新連接 WhatsApp
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          當收發訊息失效（session 斷線／殭屍連線）時，撳下面按鈕產生 QR Code，用手機 WhatsApp「已連結裝置」掃描即可重新連接。
        </p>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={startRebind}
            className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-red-700 transition"
          >
            <QrCode size={16} /> 重新連接 WhatsApp
          </button>
          <button
            onClick={handleUnbind}
            disabled={unbinding}
            className="inline-flex items-center gap-2 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition"
          >
            {unbinding ? <Loader2 size={16} className="animate-spin" /> : <Unplug size={16} />} 解除綁定
          </button>
        </div>

        {qrState !== 'idle' && (
          <div className="mt-4 border border-gray-200 dark:border-gray-700 rounded-xl p-4 bg-gray-50 dark:bg-gray-900 max-w-sm">
            {qrState === 'processing' && (
              <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 size={16} className="animate-spin" /> 正在向閘道索取 QR Code…</div>
            )}
            {qrState === 'qr' && qrImage && (
              <div className="text-center">
                <img src={qrImage} alt="WhatsApp QR Code" className="w-64 aspect-square mx-auto rounded-lg bg-white p-2" />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">用手機 WhatsApp → 設定 → 已連結的裝置 → 連結裝置，掃描此 QR（QR 會自動更新，請以最新為準）</p>
              </div>
            )}
            {qrState === 'connected' && (
              <div className="flex items-center gap-2 text-green-600 font-bold"><CheckCircle2 size={18} /> 已連線！收發通道恢復正常。</div>
            )}
            {qrState === 'error' && (
              <div className="text-red-600 text-sm">{qrMessage || '發生錯誤，請再試一次'}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminWhatsAppTab;
